import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ArrowIcon, Button, Flex } from '@modusign/design-system';
import { useGetCustomEmailBrand, useUpdateStyle } from '@modusign/openapi-client-notification';
import cx from 'classnames';
import { ChangeEvent, Suspense, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt } from 'react-router-dom';

import defaultLogoImage from '@app/assets/logos/logo-service-email-default.jpg';
import ColorPicker from '@app/components/ColorPicker';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import MouseOverGuide from '@app/components/MouseOverGuide';
import { fetchEmailStyle } from '@app/features/customBrandSetting/email';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { validateUrl } from '@app/libs/validation';
import { modalClose, modalOpen } from '@app/store/modal/actions';

import EmailPreview from './EmailPreview';

import './CustomEmailSettingModal.scss';

type CustomEmailBrand = {
  borderColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  logo: {
    height: number;
    width: number;
    url?: string;
    base64?: string;
    extension?: 'jpg' | 'jpeg' | 'png';
    link: string;
  };
  senderName: string;
  serviceDescription: string;
};

interface CustomEmailSettingModalProps {
  disableSave: boolean;
}

export default function CustomEmailSettingModal(props: CustomEmailSettingModalProps) {
  return (
    <Suspense fallback={<GlobalLoadingSpinner />}>
      <CustomEmailSettingModalContent {...props} />
    </Suspense>
  );
}

function CustomEmailSettingModalContent({ disableSave }: CustomEmailSettingModalProps) {
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();

  const { data, refetch } = useGetCustomEmailBrand(workspaceId, {
    query: { suspense: true, refetchOnMount: true },
  });
  const updateCustomEmailBrand = useUpdateStyle({
    mutation: {
      onError: createErrorHandler({ title: '맞춤 브랜딩 수정 실패' }),
      onSuccess: () => {
        refetch();
        pushDataLayer({ event: 'updateCustomEmailStyle' });
        dispatch(modalClose());
        dispatch(fetchEmailStyle());
      },
    },
  });
  const dispatch = useDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [value, setCustomEmailSetting] = useState<CustomEmailBrand>(
    // 서버에서 DTO 이름 중복 때문에 타입 오류가 납니다.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    data!.result.emailBrand
  );

  const [logoLinkError, setLogoLinkError] = useState('');

  function handleChangeBorderColor(borderColor: string) {
    setCustomEmailSetting((value) => ({ ...value, borderColor: borderColor.toUpperCase() }));
    setIsEdit(true);
  }

  function handleChangeServiceDescription(event: ChangeEvent<HTMLInputElement>) {
    const serviceDescription = event.target.value;
    setIsEdit(true);
    setCustomEmailSetting((value) => ({ ...value, serviceDescription }));
  }

  function handleChangeLogoLink(event: ChangeEvent<HTMLInputElement>) {
    const logoLink = event.target.value;

    let error = validateUrl(logoLink).message;

    if (!logoLink) {
      error = '로고 링크 URL을 입력해 주세요.';
    }

    setIsEdit(true);
    setLogoLinkError(error);
    setCustomEmailSetting((value) => ({ ...value, logo: { ...value.logo, link: logoLink } }));
  }

  function handleChangeButtonBackgroundColor(color: string) {
    setIsEdit(true);
    setCustomEmailSetting((value) => ({ ...value, buttonBackgroundColor: color.toUpperCase() }));
  }

  function handleChangeButtonTextColor(color: string) {
    setIsEdit(true);
    setCustomEmailSetting((value) => ({ ...value, buttonTextColor: color.toUpperCase() }));
  }

  function handleClickLogoChange() {
    dispatch(
      modalOpen('CUSTOM_EMAIL_SETTING_LOGO_UPLOAD_MODAL', {
        onSubmit: handleLogoChangeComplete,
      })
    );
  }

  function handleCloseModal() {
    if (isEdit && !disableSave) {
      dispatch(modalOpen('PAGE_EXIT_PREVENTION_MODAL'));
    } else {
      dispatch(modalClose());
    }
  }

  function handleLogoChangeComplete(image: {
    base64: string;
    extension: 'jpg' | 'jpeg' | 'png';
    height: number;
    width: number;
  }) {
    setIsEdit(true);
    setCustomEmailSetting((value) => ({ ...value, logo: { ...value.logo, ...image } }));

    dispatch(modalClose());
  }

  function handleSave() {
    updateCustomEmailBrand.mutate({
      workspaceId,
      data: {
        borderColor: value.borderColor,
        buttonBackgroundColor: value.buttonBackgroundColor,
        buttonTextColor: value.buttonTextColor,
        logo:
          value.logo?.base64 && value.logo.extension
            ? {
                base64: value.logo.base64.replace(/^data:image\/[a-z]+;base64,/, ''),
                extension: value.logo.extension,
                height: value.logo.height,
                width: value.logo.width,
              }
            : undefined,
        logoLink: value.logo.link,
        serviceDescription: value.serviceDescription,
      },
    });
  }

  const inputLogoLinkClassName = cx({
    InputSet: true,
    'InputSet--invalid': logoLinkError,
  });

  return (
    <>
      <div className="Container Container--max-xxl">
        <div className="Card Card--pop WindowModal WindowModal--fixed-height">
          <div className="WindowModal__header">
            <h4 className="WindowModal__header-title">
              <FontAwesomeIcon icon={faEnvelope} /> 이메일 스타일
            </h4>
            <button
              className="WindowModal__header-close-btn"
              type="button"
              onClick={handleCloseModal}
            />
          </div>
          <div className="CustomEmailSetting__container">
            <div className="CustomEmailSetting__editor box-4x">
              <div className="space-4x">
                <div className="CustomEmailSetting__label">로고 이미지</div>
                <div>
                  <img
                    alt="로고 이미지"
                    className="CustomEmailSetting__image space-1x"
                    src={value.logo.base64 || value.logo.url || defaultLogoImage}
                    style={{ width: value.logo.width }}
                  />
                </div>
                <button
                  className="TextBtn TextBtn--gray TextBtn--underline display-block"
                  name="logoChange"
                  type="button"
                  onClick={handleClickLogoChange}
                >
                  <b>
                    변경하기
                    <ArrowIcon direction="right" ml="2px" />
                  </b>
                </button>
              </div>

              <div className="space-4x">
                <div className={inputLogoLinkClassName}>
                  <div className="CustomEmailSetting__label">
                    <span>로고 링크</span>{' '}
                    <MouseOverGuide>
                      이메일 속 로고를 클릭하면
                      <br />
                      이동하는 페이지 주소를 작성해 주세요.
                    </MouseOverGuide>
                  </div>
                  <input
                    className="InputSet__text-input"
                    name="logoLink"
                    placeholder="이동할 페이지 주소"
                    type="text"
                    value={value.logo.link}
                    onChange={handleChangeLogoLink}
                  />
                  {logoLinkError && <p className="InputSet__invalidity-message">{logoLinkError}</p>}
                </div>
              </div>

              <div className="space-4x">
                <div className="InputSet">
                  <div className="CustomEmailSetting__label">
                    <span>설명글</span>{' '}
                    <MouseOverGuide>
                      오른쪽 상단에 있는 설명글을 입력해 주세요.
                      <br />
                      없을 경우 빈칸으로 두셔도 됩니다.
                    </MouseOverGuide>
                  </div>
                  <input
                    className="InputSet__text-input"
                    maxLength={30}
                    name="serviceDescription"
                    placeholder="30자 이하로 입력"
                    type="text"
                    value={value.serviceDescription}
                    onChange={handleChangeServiceDescription}
                  />
                </div>
              </div>

              <div className="space-4x">
                <div className="CustomEmailSetting__label">
                  <span>상단/하단 바 색상</span>{' '}
                  <MouseOverGuide>
                    색상 코드를 잘 모르실 경우,
                    <br />
                    도움말을 확인해주세요.{' '}
                    <a
                      className="TextBtn TextBtn--underline"
                      href="https://support.modusign.co.kr/hc/ko/articles/115015924787"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {' '}
                      자세히 알아보기
                      <ArrowIcon direction="right" ml="2px" />
                    </a>
                  </MouseOverGuide>
                </div>
                <div>
                  <ColorPicker color={value.borderColor} onChange={handleChangeBorderColor} />
                </div>
              </div>

              <div className="space-4x">
                <div className="CustomEmailSetting__label">버튼 색상</div>
                <div>
                  <ColorPicker
                    color={value.buttonBackgroundColor}
                    onChange={handleChangeButtonBackgroundColor}
                  />
                </div>
              </div>

              <div className="space-4x">
                <div className="CustomEmailSetting__label">버튼 텍스트 색상</div>
                <div>
                  <ColorPicker
                    color={value.buttonTextColor}
                    onChange={handleChangeButtonTextColor}
                  />
                </div>
              </div>
            </div>
            <div className="CustomEmailSetting__preview">
              <EmailPreview
                borderColor={value.borderColor}
                buttonBackgroundColor={value.buttonBackgroundColor}
                buttonTextColor={value.buttonTextColor}
                logo={value.logo}
                serviceDescription={value.serviceDescription}
              />
            </div>
          </div>
          {!disableSave && (
            <Flex className="WindowModal__footer" justifyContent="flex-end" py={4}>
              <Button mr={3} name="cancel" type="button" onClick={handleCloseModal}>
                취소
              </Button>
              <Button
                data-testid="custom-email-style-modal-save-button"
                disabled={!!logoLinkError || !isEdit}
                name="save"
                type="submit"
                variant="primary"
                onClick={handleSave}
              >
                저장하기
              </Button>
            </Flex>
          )}
        </div>
      </div>
      <Prompt message={'입력한 내용이 저장되지 않습니다.\n정말 나가시겠습니까?'} when={isEdit} />
    </>
  );
}
